import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Sparkles, AlertCircle, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { cn } from '../../lib/utils';

const quickPrompts = [
  "Summarize this patient's progress",
  "Create a supportive follow-up message",
  "Suggest a 2-week progression plan",
  "What might be causing plateau?"
];

export function ChatInterface({ 
  messages = [], 
  onSendMessage, 
  placeholder = "Ask AI assistant...",
  showQuickPrompts = true,
  userAvatar,
  aiName = "AI Assistant",
  disclaimer = "Assistive suggestion — clinician decides"
}) {
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSendMessage?.(input);
    setInput('');
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 1500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary-foreground" />
        </div>
        <div>
          <h3 className="font-display font-semibold text-foreground">{aiName}</h3>
          <p className="text-xs text-muted-foreground">Powered by AI</p>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto scrollbar-thin space-y-4">
        {messages.length === 0 && showQuickPrompts && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center mb-4">Try asking:</p>
            <div className="grid gap-2">
              {quickPrompts.map((prompt, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  className="justify-start text-left h-auto py-2.5 px-3 text-sm"
                  onClick={() => {
                    setInput(prompt);
                  }}
                >
                  <ChevronRight className="w-4 h-4 mr-2 text-primary flex-shrink-0" />
                  <span>{prompt}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
        
        <AnimatePresence>
          {messages.map((message, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={cn(
                'flex gap-3',
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-primary-foreground" />
                </div>
              )}
              <div className={cn(
                message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'
              )}>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                {message.role === 'assistant' && disclaimer && (
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/50">
                    <AlertCircle className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">{disclaimer}</span>
                  </div>
                )}
              </div>
              {message.role === 'user' && userAvatar && (
                <Avatar className="w-8 h-8 flex-shrink-0">
                  <AvatarImage src={userAvatar} />
                  <AvatarFallback>U</AvatarFallback>
                </Avatar>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        
        {isTyping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-3"
          >
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary-foreground" />
            </div>
            <div className="chat-bubble-ai">
              <div className="flex items-center gap-1">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            className="flex-1"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim()}
            className="px-4"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
